import * as React from 'react';
import { cn } from '@/lib/utils';

// A type alias rather than the empty `interface X extends Y {}` that input.tsx uses:
// identical to consumers, but that form trips @typescript-eslint/no-empty-object-type
// and there is no reason to add another instance of an error the repo already carries.
export type TextareaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement>;

/**
 * Textarea primitive — the multi-line counterpart to Input.
 *
 * Deliberately carries NO height class. Input pins itself to `h-11 md:h-10`, but a
 * textarea's height is driven by its content (see AnswerInput's auto-grow effect),
 * so a fixed height here would pin it to one line and defeat the point. Callers set
 * their own resting height via `rows` and their own ceiling via `max-height`.
 *
 * Everything else — radius, border, padding, text size, placeholder colour, focus
 * ring, disabled treatment — is identical to Input so the two read as one family.
 */
const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, ...props }, ref) => {
    return (
      <textarea
        className={cn(
          'flex w-full rounded-[6px] border border-gray-300 bg-white px-3 py-2 text-sm placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-otter-orange-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
          className
        )}
        ref={ref}
        {...props}
      />
    );
  }
);
Textarea.displayName = 'Textarea';

export { Textarea };
